import { useCallback, useContext, useMemo, useState } from 'react';
import { CloudDownloadOutlined, DeleteOutline as DeleteIcon, MoreVert } from '@mui/icons-material';
import { Chip, IconButton, ListItemText, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { DocumentReferencesView } from 'api';
import { DELETE_REFERENCE_CUSTOM_MESSAGE, FILE_NAME } from 'common/constants/documents';
import * as messages from 'common/constants/messages/validations';
import { ConfirmationDelete } from 'components/Dialogs';
import { DialogContext, LayoutContext } from 'context';
import useDocumentsContext from 'context/DocumentsContext';
import { useDocuments, useResponse } from 'services/hooks';
import { Logger } from 'utilities';
import getCancelDialogOptions from 'utilities/getCancelDialogOptions';
import { ReferencedDocumentProps } from './ReferencedDocument.props';
import useGetFileIconByExtension from '../../../../utilities/getFileIconByExtension/getFileIconByExtension';
import { PREVIEWABLE_DOC_TYPES } from '../../../Drawer/document-preview-drawer-tab/document-preview.constants';

const ReferencedDocument = ({
  document,
  selectedMeasurementDateId,
  currentPage,
  isDisabled,
  areDocumentsActionsDisabled,
  onClick,
  citationMetadata,
  citationNumber,
}: ReferencedDocumentProps) => {
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const { viewOnlyUser } = useContext(LayoutContext);
  const dialogContext = useContext(DialogContext);
  const { documents, updateReferencedDocuments } = useDocumentsContext();
  const { addDocumentReferencesObject, downloadDocument, setIsLoading } = useDocuments();
  const { errorNotification, successNotification } = useResponse();
  const isPreviewableFileType = useMemo(
    () => PREVIEWABLE_DOC_TYPES.includes(document.file.file_type),
    [document.file.file_type]
  );
  const currentReference = useMemo(
    () => document.referenced_in?.find(item => item.reference_object_type === currentPage),
    [document.referenced_in, currentPage]
  );

  const getReferencesData = (referencedFeatureId: number) => ({
    create_list: [],
    delete_list: [referencedFeatureId],
  });

  const handleReferences = async (): Promise<void> => {
    try {
      // Get id of the corresponding reference
      const referencedFeatureId = currentReference?.id;
      if (!referencedFeatureId) {
        Logger.error('Referenced feature ID not found', {
          document,
          currentPage,
        });
        return;
      }
      const updatedReferenceData = getReferencesData(referencedFeatureId);
      const requestData = {
        ...updatedReferenceData,
        measurement_date_id: selectedMeasurementDateId,
      };
      await addDocumentReferencesObject(selectedMeasurementDateId, requestData);
      await updateReferencedDocuments(documents, currentPage);
      queryClient.invalidateQueries({
        queryKey: ['documents'],
        exact: false,
      });
      successNotification(messages.REFERENCE_DELETED_SUCCESS);
    } catch (error) {
      errorNotification(messages.REFERENCE_DELETED_ERROR);
      Logger.error('Error deleting reference', { error });
    }
  };

  const handleClick = async (): Promise<void> => {
    const { showDialog } = dialogContext;
    const ContentComponent = (
      <ConfirmationDelete customMessage={DELETE_REFERENCE_CUSTOM_MESSAGE(document)} itemName={FILE_NAME(document)} />
    );

    showDialog(
      getCancelDialogOptions(
        () => ContentComponent,
        () => handleReferences()
      )
    );
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    handleClick();
  };

  const handleDownload = useCallback(
    async (documentParam: DocumentReferencesView) => {
      setIsLoading(true);
      await downloadDocument(documentParam);
      setIsLoading(false);
    },
    [downloadDocument, setIsLoading]
  );

  const handleDocumentClick = useCallback(() => {
    if (onClick && isPreviewableFileType && currentReference) {
      onClick(currentReference);
    } else {
      handleDownload(document);
    }
  }, [onClick, isPreviewableFileType, document, handleDownload, currentReference]);

  const formatCitationTooltip = () => `Document: ${document.file.filename}.${document.file.file_type}`;

  return (
    <Stack
      sx={{
        width: '40rem',
        '&:hover': {
          cursor: 'pointer',
        },
      }}
      direction={'row'}
      spacing={2}
      aria-label="Referenced document">
      {citationNumber !== undefined && (
        <Tooltip
          title={
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>{formatCitationTooltip()}</pre>
          }
          placement="top">
          <Chip
            label={`[${citationNumber}]`}
            size="small"
            sx={{
              height: '20px',
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '& .MuiChip-label': {
                padding: '0 6px',
              },
            }}
          />
        </Tooltip>
      )}
      {useGetFileIconByExtension(document.file.file_type)}
      <Tooltip
        disableHoverListener={areDocumentsActionsDisabled}
        title={isPreviewableFileType ? 'Click to preview.' : 'Click to download. (preview coming soon)'}
        placement="top-start">
        <ListItemText
          primary={FILE_NAME(document)}
          onClick={areDocumentsActionsDisabled ? undefined : () => handleDocumentClick()}
        />
      </Tooltip>
      <Stack direction={'row'} spacing={2}>
        <IconButton
          disabled={isDisabled || viewOnlyUser}
          onClick={() => handleDownload(document)}
          aria-label="Download"
          size="small">
          <CloudDownloadOutlined fontSize="small" />
        </IconButton>
        <IconButton
          disabled={isDisabled || viewOnlyUser}
          onClick={handleMenuClick}
          aria-label="More actions"
          size="small">
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          slotProps={{
            list: {
              'aria-labelledby': 'more-actions-button',
            },
          }}>
          <MenuItem onClick={handleDeleteClick}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      </Stack>
    </Stack>
  );
};

export default ReferencedDocument;
